import React, { useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";
import {
  vehicleOptionsDictionary,
  prefferedLanguageDictionary,
} from "../../../dictionaries";
import { DateHelper, renderRecordValue } from "../../../utils";
import {
  RIDE_ACTION_STATUSES,
  URLS,
  USER_ROLES,
  RIDE_STATUSES,
  UBER_REREQUEST_HISTORY_MARKERS,
  UBER_TEXT,
} from "../../../constants/constants";
import { useCurrentRole } from "../../../hooks/useCurrentRole";
import { AdditionalInfo } from "./RideDetailsPartials/AdditionalInfo";
import { AdditionalOptions } from "./RideDetailsPartials/AdditionalOptions";
import { VehicleInfo } from "./RideDetailsPartials/VehicleInfo";
import { CaregiversInfo } from "./RideDetailsPartials/CaregiversInfo";
import { RiderInfo } from "./RideDetailsPartials/RiderInfo";
import { RideStatusInfo } from "./RideDetailsPartials/RideStatusInfo";
import { RideDestinationInfo } from "./RideDetailsPartials/RideDestinationInfo";
import { RidePickUpInfo } from "./RideDetailsPartials/RidePickUpInfo";
import {
  convertInchesToFeet,
  getOtherLegSeqIdOfRoundtrip,
  renderPhoneNumber,
} from "../../../utils/helpers";
import { RecurringInfo } from "./RideDetailsPartials/RecurringInfo";
import styles from "./index.module.css";
import { TimeDurationInfo } from "./RideDetailsPartials/TimeDurationInfo";
import { Divider, Timeline, Tooltip, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import RideReceiptComponent from "./RideDetailsPartials/RideBillDetails/RideBillDetails";
import { Snackbar } from "../Snackbar/Snackbar";
import { OverNightRide } from "./RideDetailsPartials/OverNightRide";
import { ArrowIcon, totalCaregiversIcon } from "../../../assets";
import { UberRideInfo } from "./RideDetailsPartials/UberRideInfo";
import { UberReceiptInfo } from "./RideDetailsPartials/UberReceiptInfo";
import { UberLiveMapInline } from "../UberDriverMap/UberLiveMapInline";
import { isDriverRemainOnSiteActive, isDriverRemainOnSiteSchedulingWindow } from "../../../utils/RideHelper";
import { DriverOnSiteNotice } from "../DriverOnSiteNotice/DriverOnSiteNotice";
import { isDevOrStageHost } from "../../../utils/platformHelper";
import { RecurringRidePlanSummary } from "../RecurringRidePlanSummary/RecurringRidePlanSummary";

const getRideHistoryDisplayAction = (item) =>
  item?.rideAction === "Canceled" ? "Canceled" : item?.rideAction;

const filterConsecutiveDuplicateRideHistory = (history = []) => {
  if (!history.length) return [];

  return history.filter((item, index) => {
    if (index === 0) return true;
    return (
      getRideHistoryDisplayAction(item) !==
      getRideHistoryDisplayAction(history[index - 1])
    );
  });
};

const lastSeriesRideDisclaimerInformation = {
  type: "error",
  title: "Last Ride in Series: Renew to Continue Service",
  description:
    "This is the last ride in the current series. If this rider needs additional rides, please renew to keep their transportation services active.",
  actionBtn: "Renew Ride Series",
};

export const Record = ({ title, value, myClassName, valueClassName }) =>
  value ? (
    <p
      className={classnames({
        [styles.record]: true,
        [myClassName]: myClassName,
      })}
    >
      <span className={styles.recordTitle}>{title}:</span>
      <span className={valueClassName}>{value}</span>
    </p>
  ) : null;

export const RideDetails = ({
  values,
  showFullInfo,
  handleRideStatusModal,
  airportBufferTime,
  showPrefferedLanguageBeforeAccept,
  showRecurringRidePlan,
  role,
  tcConfiguration,
  handleRideRenewal,
}) => {
  const { isTCWorker, isHOWorker, isMHAdmin, isHOAdmin, isTCAdmin, isEnterpriseAdmin } = useCurrentRole(role);
  const [showDriverMap, setShowDriverMap] = React.useState(false);
  const [showUberRideId, setShowUberRideId] = useState(false);

  useEffect(() => {
    setShowUberRideId(Boolean(isMHAdmin));
  }, [isMHAdmin]);

  const caregivers = values?.pickupPointAdditionalInfo?.caregivers || [];
  const additionalPassengers = values?.pickupPointAdditionalInfo?.additionalPassengers || 0;
  const passengersTotal = (
    <div className={styles.caregiverPassengersTotal}>
      <span className={styles.caregiverPassengersTotalLabel}>Total passengers:</span>
      <span className={styles.caregiverPassengersTotalValue}>
        <img src={totalCaregiversIcon} alt="" width={17} height={17} />
        {additionalPassengers + 1}
      </span>
    </div>
  );
  
  const isUberRide = values?.vehicleType === 'uberAmbulatoryRide';

  const uberRoundtripDriverNotes = useMemo(() => {
    if (!isUberRide || !values?.isRoundtrip) {
      return null;
    }
    const legA =
      values.driverNotesLegA ||
      values.additionalInformation?.driverNotesLegA ||
      (values.leg === "B" ? values.connectedLeg?.rideNotes : values.rideNotes) ||
      "";
    const legB =
      values.driverNotesLegB ||
      values.additionalInformation?.driverNotesLegB ||
      (values.leg === "B" ? values.rideNotes : values.connectedLeg?.rideNotes) ||
      "";
    return { legA, legB };
  }, [isUberRide, values]);
  const uberDriver = values?.uberInfo?.driver;
  const effectiveDriver = (isUberRide && uberDriver)
    ? {
        firstName:   uberDriver.name?.split(' ')[0] || uberDriver.name || '',
        lastName:    uberDriver.name?.split(' ').slice(1).join(' ') || '',
        phoneNumber: uberDriver.phone || null,
        email:       null,
      }
    : (values?.driver || null);
  
  const vehicleInformationRadioOpts = useMemo(() => {
    return [
      {
        name: "Wheelchair needs to be provided by the driver",
        value: values?.pickupPointAdditionalInfo?.isWheelchairNeeded,
      },
      {
        name: "Has a motorized wheelchair",
        value: values?.pickupPointAdditionalInfo?.hasMotorizedWheelchair,
      },
      {
        name: "Has stairs in or outside the home",
        value: values?.pickupPointAdditionalInfo?.hasStairs,
      },
      {
        name: "Rider is traveling with someone who uses a car seat or booster seat",
        value: values?.pickupPointAdditionalInfo?.needsCarSeat,
      },
      {
        // name: "1 Car seat needs to be provided by the driver",
        // value: values?.pickupPointAdditionalInfo?.needsAdditionalCarSeat,

        name:
        values?.pickupPointAdditionalInfo?.needsAdditionalCarSeat
          ? values?.pickupPointAdditionalInfo?.carSeatByDriver
            ? `${values?.pickupPointAdditionalInfo?.carSeatByDriver} Car seat needs to be provided by the driver`
            : values?.pickupPointAdditionalInfo?.carSeatByRider
            ? `${values?.pickupPointAdditionalInfo?.carSeatByRider} Car seat needs to be provided by the rider`
            : "1 Car seat needs to be provided by the driver"
          : null,
      value: values?.pickupPointAdditionalInfo?.needsAdditionalCarSeat,
      },
      // {
      //   name: `Car seats will be brought by the rider ${values?.pickupPointAdditionalInfo?.carSeatByRider}`,
      //   value: values?.pickupPointAdditionalInfo?.carSeatByRider,
      // },
      
      // {
      //   name: `The total number of car seats must be provided by the driver: ${values?.pickupPointAdditionalInfo?.carSeatByDriver}`,
      //   value: values?.pickupPointAdditionalInfo?.carSeatByDriver,
      // },
      // {
      //   name: "1 Booster seat needs to be provided by the driver",
      //   value: values?.pickupPointAdditionalInfo?.needsBoosterSeat,
      // },
      {
      name:
        values?.pickupPointAdditionalInfo?.needsBoosterSeat
          ? values?.pickupPointAdditionalInfo?.boosterSeatByDriver > 0
            ? `${values?.pickupPointAdditionalInfo?.boosterSeatByDriver} Booster seat needs to be provided by the driver`
            : values?.pickupPointAdditionalInfo?.boosterSeatByRider > 0
            ? `${values?.pickupPointAdditionalInfo?.boosterSeatByRider} Booster seat needs to be provided by the rider`
            : "1 Booster seat needs to be provided by the driver"
          : null,
      value: values?.pickupPointAdditionalInfo?.needsBoosterSeat,
    },
      // {
      //   name: `Booster seats will be brought by the rider ${values?.pickupPointAdditionalInfo?.boosterSeatByRider}`,
      //   value: values?.pickupPointAdditionalInfo?.boosterSeatByRider,
      // },
      // {
      //   name: `The total number of booster seats must be provided by the driver: ${values?.pickupPointAdditionalInfo?.boosterSeatByDriver}`,
      //   value: values?.pickupPointAdditionalInfo?.boosterSeatByDriver,
      // },
      {
        name: "Rider needs driver escort from pick-up location to van",
        value: values?.pickupPointAdditionalInfo?.needsAdditionalCarSeat,
      },
      {
        name: "Rider needs driver escort from van to drop-off location",
        value: values?.pickupPointAdditionalInfo?.needsAdditionalCarSeat,
      },
      {
        name: "Rider is being picked up as a result of a discharge from a facility",
        value: values?.pickupPointAdditionalInfo?.dischargeFromFacility,
      },
    ];
  }, [values]);

  const timelineItemTail = "#56A475";

  const showRoundTripHeading = useMemo(() => {

    if(values?.isRoundtrip && !values?.leg)
    {
      return true;
    }else if(values?.isRoundtrip && values?.connectedLeg)
    {
      return true;
    }

    return false;
  },[values]);

  const showDriverOnSiteBetweenLegs = useMemo(() => {
    if (isDriverRemainOnSiteActive(values)) return true;
    if (!values?.isRoundtrip || !values?.driverRemainOnSite) return false;
    return isDriverRemainOnSiteSchedulingWindow(values?.pickUpDate, values?.returnTime);
  }, [values]);

  const timelineItems = useMemo(() => {
    if (!values?.rideHistory) return [];

    const isUberRideHistory = values?.vehicleType === "uberAmbulatoryRide";
    const rideHistoryToDisplay = filterConsecutiveDuplicateRideHistory(values.rideHistory);

    return rideHistoryToDisplay.map((item) => {
      const hoverItem = `${item.comment ? item.comment : ""}`;
      const commentText = hoverItem.toLowerCase();
      const statusText = (item.rideAction || "").toLowerCase();
      const isUberReRequest =
        isUberRideHistory &&
        (commentText.includes(UBER_REREQUEST_HISTORY_MARKERS.COMMENT_REREQUESTED_FROM_RIDE) ||
          commentText.includes(UBER_REREQUEST_HISTORY_MARKERS.COMMENT_RESUBMITTED_TO_UBER) ||
          statusText.includes(UBER_REREQUEST_HISTORY_MARKERS.STATUS_RESUBMITTED_TO_UBER));

      return {
        title: DateHelper.renderDateTimeWithTZ({
          dateTime: item.createdAt,
          timeZone: values?.timezone,
          useFullFormattedDate: true
        }),
        content: (
          <>
            {hoverItem !== "" ? (
              <Tooltip
                color={timelineItemTail}
                title={
                  <span
                    className={classnames({
                      [styles.tooltipContent]: true,
                      [styles.itemPos]: true,
                    })}
                  >
                    {hoverItem}
                  </span>
                }
              >
                {item.newStatus.toLowerCase().includes("dispute") ? (
                  <span>
                    <b>Change Request Status: </b>
                  </span>
                ) : (
                  ""
                )}
                {isUberReRequest ? (
                  <Tag className={styles.uberReRequestTag}>
                    {UBER_TEXT.REREQUEST_TAG_LABEL}
                  </Tag>
                ) : (
                  <span
                    className={hoverItem ? styles.rideActionPointer : ""}
                  >
                    {item.rideAction !== "Canceled"
                      ? item.rideAction
                      : "Canceled"}
                  </span>
                )}
                <br />
              </Tooltip>
            ) : (
              <>
                {isUberReRequest ? (
                  <>
                    <Tag className={styles.uberReRequestTag}>
                      {UBER_TEXT.REREQUEST_TAG_LABEL}
                    </Tag>
                    <br />
                  </>
                ) : (
                  <>
                    <span>
                      {item.rideAction !== "Canceled"
                        ? item.rideAction
                        : "Canceled"}
                    </span>
                    <br />
                  </>
                )}
              </>
            )}
            <span className={styles.detailFont}>
              <span className={styles.singleLine}><b>Name:</b> {item.rideAction === RIDE_ACTION_STATUSES.REDEEMED_BY_RIDER && values?.riderInfo ? values.riderInfo.firstName+' '+values.riderInfo.lastName : item.user ? item.user.firstName+' '+item.user.lastName : 'System' } </span>
              <span className={styles.singleLine}>
                <b>Organization:</b>{" "}
                {item?.user?.role === USER_ROLES.SUPER_ADMIN
                  ? item.organization
                    ? `MedHaul (for ${item.organization.name})`
                    : "MedHaul"
                  : item.organization?.name || "MedHaul"}
              </span>
              <span className={styles.singleLine}><b>Title:</b> {item?.user?.role === USER_ROLES.SUPER_ADMIN ? 'MH Admin' : item.user?.role || 'MedHaul'} </span>
              <span className={styles.singleLine}><b>Phone Number:</b> {renderPhoneNumber(item.user?.phoneNumber)} </span>
              {item.rideAction === RIDE_ACTION_STATUSES.CREATED && <span className={styles.singleLine}><b>Email:</b> {item.user?.email} </span>}
              {item?.notifications?.length > 0 && (
                <span className={styles.singleLine}>
                  <b>Notifications: </b>
                    {item?.notifications?.map((element) => (
                      <Tooltip
                        key={element.id}
                        color={timelineItemTail}
                        title={
                          <span
                            className={classnames({
                              [styles.tooltipContent]: true,
                              [styles.itemPos]: true,
                                                          })}
                          >
                            {<div dangerouslySetInnerHTML={{ __html: element.body }} />}
                          </span>
                        }
                      >
                        <span
                          className={classnames({
                            [styles.notificationItem]: true,
                            [styles.rideActionPointer]: true,
                          })}
                        >
                          {element.messageType} - {element.recipient}
                        </span>
                        <br />
                      </Tooltip>
                    ))}
                </span>
              )}

            </span>
          </>

        ),
        color: item.rideAction === RIDE_ACTION_STATUSES.RESUBMITTED ? '#315e45' : timelineItemTail,
      };
    });
  }, [values?.rideHistory, values?.timezone, values?.vehicleType]);
  
  const medhaulStatus = values?.status;
  const isMapInProgress = medhaulStatus === RIDE_STATUSES.IN_PROGRESS;
  const isMapCompleted = medhaulStatus === RIDE_STATUSES.COMPLETED;
  const hasAddresses = values?.pickupAddressDetails?.formattedAddress && values?.destinationAddress?.formattedAddress;
  const showMap = (isMHAdmin || isHOAdmin || isHOWorker) && isUberRide && (isMapInProgress || isMapCompleted) && hasAddresses;
  const showHistory = role === USER_ROLES.SUPER_ADMIN && values?.rideHistory?.length > 0;

  const sidePanel = (showMap || showHistory) ? (
    <>
      <Divider orientation="vertical" className={styles.divider} />
      <div className={styles.column2}>
        {showMap && (
          <UberLiveMapInline
            rideId={values?.id}
            pickupAddress={values.pickupAddressDetails.formattedAddress}
            destinationAddress={values.destinationAddress.formattedAddress}
            lastLocation={values?.uberInfo?.lastLocation}
            apiUrl={process.env.REACT_APP_API_URL}
            isCompleted={isMapCompleted}
          />
        )}
        {showHistory && (
          <>
            <h2 className={styles.rideHistory}>Ride History</h2>
            <Timeline mode="start" items={timelineItems} />
          </>
        )}
      </div>
    </>
  ) : null;

  console.log('values?.status', values?.status);
  return (
    <div className={styles.container}>
    <div className={classnames({ [styles.rideDetails]: true,  [styles.column1] :true, })} >
      {values?.isRoundtrip && values?.connectedLegId && (values?.connectedLeg?true:!isTCWorker)  && (
        <p className={styles.otherLeg}>
          Other leg of the ride –{" "}
          <NavLink
            className={styles.link}
            to={`${URLS.RIDES}/${values?.connectedLegId}`}
          >
            ID{" "}
            {renderRecordValue(
              getOtherLegSeqIdOfRoundtrip(values?.seqId, values?.leg)
            )}
          </NavLink>
        </p>
      )}
      {(isHOWorker || isMHAdmin) && values?.isLastRecurring && (
        <>
        <div className={styles.latestSnackbar}>
          <Snackbar
            type={lastSeriesRideDisclaimerInformation.type}
            title={lastSeriesRideDisclaimerInformation.title}
            content={lastSeriesRideDisclaimerInformation.description}
            actionBtn={isHOWorker?lastSeriesRideDisclaimerInformation.actionBtn: undefined}
            handleActionBtn={handleRideRenewal}
          />

        {
        values?.isRecurring && values?.recurringRideIndex && !isTCWorker && (
          <RecurringInfo info={values?.recurringRideIndex} />
          )
        }
        </div>
        <Record
          title="Request Created"
          value={renderRecordValue(
            values?.createdAt &&
              DateHelper.renderDateTimeWithTZ({
                dateTime: values?.createdAt,
                timeZone: values?.pickUpDate.timezone,
              })
          )}
          myClassName={styles.createdAt}
        />
        <Record
          title="Created By"
          value={`${renderRecordValue(
            values?.createdBy?.firstName
          )} ${renderRecordValue(values?.createdBy?.lastName)}`}
        />
      </>
      )}
      <div className={styles.rideDetailsHead}>
        {showFullInfo && (
          <div className={styles.riderInformation}>
            <RiderInfo
              rider={values?.riderInfo}
              showFullInfo={showFullInfo}
              isHOWorker={isHOWorker}
              isMHAdmin={isMHAdmin}
            />
          </div>
        )}

        
      </div>
      <VehicleInfo
        vehicleType={renderRecordValue(
          vehicleOptionsDictionary[values?.vehicleType]
        )}
        status={Boolean(values?.readableStatus)}
        isUberRide={isUberRide}
        uberRideId={showUberRideId ? values?.uberInfo?.uberRideId : undefined}
      />

      {showFullInfo && !values?.status && passengersTotal}

      {values?.status && (
        <RideStatusInfo
          riderInfo={values?.riderInfo}
          driver={effectiveDriver}
          status={values?.status}
          vehicleType={values?.vehicleType}
          readableStatus={values?.readableStatus}
          isReRequested={values?.isReRequested}
          showFullInfo={showFullInfo}
          handleRideStatusModal={handleRideStatusModal}
          role={role}
          startDate={{
            datetime: values?.startDate,
            timezone: values?.pickUpDate?.timezone,
          }}
          endDate={{
            datetime: values?.endDate,
            timezone: values?.pickUpDate?.timezone,
          }}
          rideWaitTime={values?.waitTime}
          driverWaitTimeMinutes={values?.driverWaitTimeMinutes}
          driverArrivalTime={values?.driverArrivalTime ? {
            datetime: values?.driverArrivalTime,
            timezone: values?.pickUpDate?.timezone,
          } : null}
          endRideComment={values?.endRideComment}
          isTCWorker={isTCWorker}
          isHOWorker={isHOWorker}
          isMHAdmin={isMHAdmin}
          timezone={values?.pickUpDate?.timezone}
          cancellationInfo={values?.cancellationInfo}
          unableToFulfillInfo={values?.unableToFulfillInfo}
          declineInfo={values?.declineInfo}
          uberInfo={values?.uberInfo}
          rideCurrentAction={values?.rideCurrentAction}
          connectedLeg={values?.connectedLeg}
        />
      )}

      {values?.status && passengersTotal}

        {isTCWorker && !showFullInfo && values?.riderInfo?.weight && (
          <Record
            title="Weight, lb(s)"
            value={renderRecordValue(values?.riderInfo?.weight)}
          />
        )}

        {isTCWorker && !showFullInfo && values?.riderInfo?.height && (
          <Record
            title="Height (Feet/Inches)"
            value={convertInchesToFeet(values?.riderInfo?.height)}
          />
      )}
      {showPrefferedLanguageBeforeAccept && (
        <Record
          title="Rider's Preferred Language"
          value={renderRecordValue(
            prefferedLanguageDictionary[values?.riderInfo?.preferredLanguage]
          )}
          myClassName={styles.ridersPreferredLanguage}
        />
      )}
      
      {values?.isOvernightRide && (
          <div className={classnames({[styles.d_flex]:true, [styles.mt5]:true})}>
             <span className={styles.recordTitle}>{'Schedule'}:</span>
        <OverNightRide/>
        </div>
      )}
      
      <AdditionalOptions
        rideOptions={values?.rideOptions || []}
        vehicleInformationRadioOpts={vehicleInformationRadioOpts}
      />

      {showFullInfo &&
        values?.pickupPointAdditionalInfo?.caregivers?.length > 0 && (
            <div className={styles.caregiverInfo}>
              <CaregiversInfo
                title="Caregiver"
                caregivers={values?.pickupPointAdditionalInfo?.caregivers}
                showFullInfo={showFullInfo}
              />
            </div>
        )}
      {(showRoundTripHeading) && <h3 className={styles.roundTripHeader}>Roundtrip Ride</h3>}
      {showDriverOnSiteBetweenLegs && (
        <DriverOnSiteNotice className={styles.mt5} />
      )}
      <div className={styles.routes}>
        <RidePickUpInfo
          pickupAddressDetails={values?.pickupAddressDetails}
          pickUpDate={values?.pickUpDate}
          riderDescription={values?.riderDescription}
          pickUpDateLowerLimit={values?.pickUpDateLowerLimit}
          isPickUpDateFlexible={values?.isPickUpDateFlexible}
          tcEstimatedPickUpDate={values?.tcEstimatedPickUpDate}
          securityGate={values?.pickUpSecurityGateDetails}
          flightInfo={values?.flightInfo}
          airportBufferTime={airportBufferTime}
          appointmentDate={values?.appointmentDate}
          isRecurringRide={values?.isRecurring}
          leg={values?.isRoundtrip ? (values?.leg  ? values?.leg:'A'):''}
          seqId={values?.seqId}
          destinationAddress={values?.isRoundtrip ? values?.destinationAddress : null}
        />

        <div className={styles.destionationRouteInfo}>
          <div className={styles.arrows}>
            { !values?.connectedLeg && (
            (values?.isRoundtrip  && !values?.leg)  ? (
              <>
                {/* <ArrowIcon fill="#bdbdbd" />
                <ArrowIcon fill="#bdbdbd" className={styles.arrowLeft} /> */}
              </>
            ) : (
              <ArrowIcon stroke="#bdbdbd" />

            ))
            }
          </div>

          <RideDestinationInfo
            destinationAddress={values?.destinationAddress}
            originAddress={values?.isRoundtrip ? values?.pickupAddressDetails : null}
            showRoundtripInfo={
              values?.isRoundtrip && values?.connectedLeg ? true : (!isTCWorker && !values?.leg)
            }
            securityGate={values?.destinationSecurityGateDetail}
            returnTime={values?.connectedLeg?values?.connectedLeg?.pickUpDate:values?.returnTime}
            returnTimeLowerLimit={values?.returnTimeLowerLimit}
            isReturnDateFlexible={values?.isReturnDateFlexible}
            leg={values?.isRoundtrip ? (values?.connectedLeg ? values?.connectedLeg?.leg: 'B'):''}
            seqId={values?.connectedLeg?.seqId}
          />
        </div>
      </div>

      {showRecurringRidePlan && (
        <RecurringRidePlanSummary
          isRecurring={values?.isRecurring}
          isRoundtrip={values?.isRoundtrip}
          recurrenceRule={values?.recurrenceRule}
          pickUpDate={values?.pickUpDate}
          variant="review"
        />
      )}

        {values?.estMileage && (
          <TimeDurationInfo
            values={values}
            isTCWorker={isTCWorker}
            isMHAdmin={isMHAdmin}
            tcConfiguration={tcConfiguration}
          />
        )}

      

      {isUberRide && (isMHAdmin || isHOAdmin || isEnterpriseAdmin) && (
        <UberReceiptInfo
          rideReceipt={values?.rideReceipt}
          receipt={values?.uberReceipt}
          uberReceiptData={values?.uberReceiptData}
          uberInfo={values?.uberInfo}
          seqId={values?.seqId}
          rideId={values?.id}
          vehicleType={values?.vehicleType}
          userRole={role}
        />
      )}


{/* 
 UberRideInfo — dev/stage/localhost only (hidden in production).
 Trip status, driver info, track driver, SUPER_ADMIN simulate webhook. */}
      {isDevOrStageHost() && isUberRide && values?.uberInfo && (
        <UberRideInfo
          rideId={values?.id}
          rideReceipt={values?.rideReceipt}
          uberInfo={values?.uberInfo}
          uberReceipt={values?.uberReceipt}
          uberReceiptData={values?.uberReceiptData}
          onTrackDriver={(rideId) => setShowDriverMap(true)}
          showUberRideId={showUberRideId}
        />
      )}

      {uberRoundtripDriverNotes &&
      (uberRoundtripDriverNotes.legA || uberRoundtripDriverNotes.legB) ? (
        <>
          {uberRoundtripDriverNotes.legA ? (
            <div className={styles.otherInformation}>
              <label className={styles.originAddress}>
                <span>Message To Driver Leg A</span>
              </label>
              <p>{uberRoundtripDriverNotes.legA}</p>
            </div>
          ) : null}
          {uberRoundtripDriverNotes.legB ? (
            <div className={styles.otherInformation}>
              <label className={styles.originAddress}>
                <span>Message To Driver Leg B</span>
              </label>
              <p>{uberRoundtripDriverNotes.legB}</p>
            </div>
          ) : null}
        </>
      ) : (
        values?.rideNotes && (
          <div className={styles.otherInformation}>
            <label className={styles.originAddress}>
              <span>
                {isUberRide ? "Note to Uber Driver" : "Other information /Notes:"}
              </span>
            </label>
            <p>{values?.rideNotes}</p>
          </div>
        )
      )}
      {values?.additionalInformation && (
        <AdditionalInfo
          additionalFundingSource={
            values?.additionalInformation?.additionalFundingSource
          }
          costCenter={values?.additionalInformation?.costCenter}
          documentReasonForRide={
            values?.additionalInformation?.documentReasonForRide
          }
          otherReason={values?.additionalInformation?.otherReason}
        />
      )}

      {values?.status !== RIDE_STATUSES.UNFULFILLED && values?.status !== RIDE_STATUSES.RESCHEDULED &&
       !isUberRide && role !== USER_ROLES.HO_EMPLOYEE && (
        <RideReceiptComponent
          rideReceipt={values?.rideReceipt}
          actMileage={values?.actMileage}
          tcReceipt={values?.tcReceipt}
          seqId={values?.seqId}
          role={role}
          isHOAdmin={isHOAdmin}
          isMHAdmin={isMHAdmin}
          isTCAdmin={isTCAdmin} 
          isEnterpriseAdmin={isEnterpriseAdmin}
          rideStatus={values?.status}
        />
      )}

      </div>

    {sidePanel}

</div>
  );
};

RideDetails.propTypes = {
  handleSave: PropTypes.func,
  values: PropTypes.object,
  step: PropTypes.number,
  handlePreviousStep: PropTypes.func,
  handleSaveAndClose: PropTypes.func,
  airportBufferTime: PropTypes.oneOfType([PropTypes.number, PropTypes.any]),
};
